import React from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, CheckCircle, ArrowLeft, Lightbulb, FileText, MessageSquare, ClipboardCheck, Mail, HelpCircle, DollarSign, Clock, AlertTriangle, Scale } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const OffersEndgameSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  
  const offerFirstSteps = [
    "Respond promptly to acknowledge the offer",
    "Thank the startup and express appreciation",
    "Politely request a few days to decide (if needed)"
  ];

  const negotiationReasons = [
    "The offer is lower than expected or advertised",
    "You've received higher offers elsewhere",
    "You have relevant experience at a higher salary"
  ];

  const negotiationApproaches = [
    {
      title: "Ask for a review clause",
      example: "e.g. salary revisit after probation"
    },
    {
      title: "Ask for a higher starting salary",
      example: ""
    }
  ];

  const equityKeyPoints = [
    "Most startups offer options with a 4-year vesting schedule and 1-year cliff",
    "You usually don't \"own\" any shares until after year 1",
    "You typically \"exercise\" your options when the company is acquired or raises a round",
    "Expect dilution — but this is normal and often outweighed by company growth"
  ];

  const contractChecklist = [
    { item: "Line manager", detail: "is it who you expected?" },
    { item: "Salary", detail: "does it match expectations and market?" },
    { item: "Holiday", detail: "would usually expect at least 20 days + bank holidays" },
    { item: "Pension", detail: "would usually expect a minimum 3% employer contribution" },
    { item: "Equity / options", detail: "vesting schedule and % or value clearly stated" },
    { item: "Probation", detail: "usually 3-6 months" }
  ];

  const withdrawalSteps = [
    "Let Jumpstart know first at info@jumpstart-uk.com",
    "Try to give at least 24 hours' notice if an interview is already scheduled",
    "Always be polite and professional — how you exit matters"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#02B491]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <PartyPopper className="w-10 h-10 text-[#02B491]" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Offers & what comes next
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Navigate offers, negotiate terms, and make the right career decision.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={onBack}
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-colors ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-lg'
                  : 'bg-[#02B491] text-white hover:bg-[#029078] shadow-md'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-12">
          
          <StepCard title="🎉 If you receive an offer: what to do next">
            <div className="bg-[#02B491]/5 rounded-xl p-6 border border-[#02B491]/20 mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">First step: let Bethan know so the Jumpstart team can support you.</h4>
            </div>
            
            <p className="text-gray-600 mb-4">
              Even if you're still unsure, always:
            </p>
            <ul className="space-y-3 mb-6">
              {offerFirstSteps.map((step, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-gray-100 pt-6 mt-6">
              <p className="text-gray-600 text-sm italic">
                If you're not sure about anything, ask to speak to your future manager again or hop on a clarifying call with the team. Jumpstart can help review your contract or offer before you sign.
              </p>
            </div>
          </StepCard>

          <StepCard title="💰 Negotiating salary or options">
            <p className="text-gray-600 mb-6">
              It's completely okay to negotiate — just be respectful, clear and constructive.
            </p>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Reasons you might want to negotiate:</h4>
                <ul className="space-y-3 mb-6">
                  {negotiationReasons.map((reason, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-100 pt-4 mt-6">
                <p className="text-gray-700 text-sm">
                  <span className="font-semibold text-gray-800">💡 Pro tip:</span> If you're negotiating, always try to do it over a call — <span className="font-semibold">tone and intent come across much better</span> than in writing.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Two common approaches:</h4>
                <div className="space-y-4 mb-6">
                  {negotiationApproaches.map((approach, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4">
                      <h5 className="font-semibold text-gray-900">{i + 1}. {approach.title}</h5>
                      {approach.example && <p className="text-gray-600 text-sm mt-1">{approach.example}</p>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h4 className="font-semibold text-green-900 mb-4">Suggested phrasing:</h4>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-green-800 italic">
                      "I'm really excited about the role and would love to accept, but I was hoping to discuss whether there's room to increase the starting salary or include a review after 6 months."
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-green-800 italic">
                      "I've previously earned £X and while I'm excited about this opportunity, I was hoping for something in the £X-Y range — is there flexibility here?"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="Understanding startup equity and options">
            <p className="text-gray-600 mb-6">
              If your offer includes equity or options, key things to know:
            </p>
            
            <ul className="space-y-3 mb-6">
              {equityKeyPoints.map((point, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-gray-100 pt-6 mt-6">
              <h4 className="font-semibold text-gray-900 mb-3">Benchmarks:</h4>
              <p className="text-gray-700">
                Options for early-stage roles tend to range from <strong>£2,000 - £30,000 value</strong> OR <strong>0.075% - 0.5% equity</strong>
              </p>
              <p className="text-gray-600 text-sm mt-3 italic">
                Want help decoding your options offer? Just ask the Jumpstart team.
              </p>
            </div>
          </StepCard>

          <StepCard title="Sequencing offers or buying yourself time">
            <p className="text-gray-600 mb-6">
              If you've received an offer but are still waiting on others, here are two sample emails:
            </p>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">To stall the startup who offered:</h4>
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <p className="text-yellow-800 italic">
                    "Thanks so much for the offer — I'm really excited. I'm currently wrapping up interviews with a couple of other teams and wanted to see if I could have until [date] to make a decision. I really appreciate the flexibility!"
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">To accelerate another startup:</h4>
                <div className="pl-4 border-l-2 border-gray-200">
                  <p className="text-gray-700 italic">
                    "Just wanted to flag that I've received an offer elsewhere and they've asked for a decision by [date]. If I were offered a role here, I'd be very keen to accept — is there any chance we could move things forward?"
                  </p>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="Understanding your contract before signing">
            <p className="text-gray-600 mb-6">
              Double check the offer or contract for:
            </p>
            
            <div className="space-y-3 mb-6">
              {contractChecklist.map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>{item.item}</strong> — {item.detail}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-[#02B491]/5 rounded-xl p-6 border border-[#02B491]/20">
              <p className="text-gray-700">
                <strong>Need help reviewing?</strong> Reach out to Bethan at <a 
                  href="mailto:bethan@jumpstart-uk.com" 
                  className="text-[#02B491] hover:underline"
                >
                  bethan@jumpstart-uk.com
                </a>
              </p>
            </div>
          </StepCard>

          <StepCard title="Following up if you haven't heard back">
            <div className="space-y-4 mb-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-yellow-700 font-semibold text-sm">48h</span>
                </div>
                <span className="text-gray-700">send a polite follow-up</span>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-red-700 font-semibold text-sm">3d+</span>
                </div>
                <span className="text-gray-700">let the Jumpstart team know by emailing info@jumpstart-uk.com</span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6 mt-6">
              <h4 className="font-semibold text-gray-900 mb-3">Example follow-up email:</h4>
              <div className="pl-4 border-l-2 border-gray-200">
                <p className="text-gray-700 italic">
                  "Hi [name], just checking in after our interview last [day] — I really enjoyed our conversation and wanted to see if there are any updates on next steps. Let me know if you need anything else from me!"
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="How to withdraw from a process (at any stage)">
            <p className="text-gray-600 mb-6">
              If the role no longer feels like the right fit:
            </p>
            
            <ul className="space-y-3 mb-6">
              {withdrawalSteps.map((step, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#02B491] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ul>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Example email (during interview process):</h4>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-gray-700 italic">
                    "Hi [name], thank you again for the time and conversations so far. After reflecting on the opportunity, I've decided to focus my search elsewhere and would like to withdraw from the process. I really appreciate your time and hope to stay in touch."
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Example email (post-offer — use to book a call):</h4>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-gray-700 italic">
                    "Hi [name], I really appreciate the offer and all the time the team has invested in the process. I'd love to find a time to jump on a quick call to chat through where my head is at before making a final decision. Would [times] work?"
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mt-6">
              <p className="text-blue-800 text-sm">
                <strong>If in doubt we can help</strong> — just reach out to <strong>info@jumpstart-uk.com</strong>
              </p>
            </div>
          </StepCard>

        </div>
        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersEndgameSection;